/**
 * Age Verification Callback Page
 * Handles the redirect from PassportReader after verification
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  ShieldCheck,
  ShieldAlert,
  Loader2,
  Ticket,
  RefreshCw,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const processedRef = useRef(false);
  
  const sessionId = searchParams.get('session_id');
  const attendeeId = searchParams.get('attendee_id');
  
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Process the callback
  useEffect(() => {
    const processCallback = async () => {
      // Prevent double processing
      if (processedRef.current) return;
      processedRef.current = true;
      
      if (!session?.access_token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      
      if (!sessionId || !attendeeId) {
        setError('Missing verification information');
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/age-verification/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            session_id: parseInt(sessionId),
            attendee_id: attendeeId
          })
        });
        
        const data = await response.json();
        setResult(data);
      } catch (err) {
        console.error('Callback processing error:', err);
        setError('Failed to process verification result');
      } finally {
        setLoading(false);
      }
    };
    
    // Small delay to ensure auth is ready
    const timer = setTimeout(processCallback, 500);
    return () => clearTimeout(timer);
  }, [session?.access_token, sessionId, attendeeId]);
  
  // Get event ID for retry
  const getEventId = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/age-verification/status/${attendeeId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      const data = await response.json();
      return data.event_id;
    } catch {
      return null;
    }
  };
  
  const handleRetry = async () => {
    // Get event_attendee details to get event_id
    try {
      const response = await fetch(`${API_URL}/api/door-staff/scan?qr_data={}`, {
        headers: { Authorization: `Bearer ${session?.access_token}` }
      });
      // Just navigate back to tickets for now
      navigate('/app/my-tickets');
    } catch {
      navigate('/app/my-tickets');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-electric-ice-blue animate-spin mx-auto mb-4" />
          <p className="text-cloud-white text-lg mb-2">Processing Verification</p>
          <p className="text-soft-silver">Please wait while we confirm your age...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <Card className="max-w-md bg-graphite/50 border-coral-red/30">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-coral-red mx-auto mb-4" />
            <h1 className="text-xl font-bold text-cloud-white mb-2">Verification Error</h1>
            <p className="text-soft-silver mb-6">{error}</p>
            <Button onClick={() => navigate('/app/my-tickets')}>
              Go to My Tickets
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Success - Age Verified
  if (result?.success && result?.status === 'pass') {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6" data-testid="verify-callback-success">
        <Card className="max-w-md bg-graphite/50 border-cool-mint/30">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-24 h-24 rounded-full bg-cool-mint/10 flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-12 h-12 text-cool-mint" />
            </div>
            <h1 className="text-2xl font-bold text-cloud-white mb-2">Age Verified!</h1>
            <p className="text-soft-silver mb-6">
              Your age has been successfully verified. Your ticket is now ready to use.
            </p>
            
            {result.calculated_age && (
              <div className="bg-obsidian/50 rounded-xl p-4 mb-6">
                <p className="text-muted-text text-sm">Age Confirmed</p>
                <p className="text-2xl font-bold text-cloud-white">
                  {result.calculated_age}+ years old
                </p>
              </div>
            )}
            
            <Button
              size="lg"
              className="w-full bg-electric-ice-blue hover:bg-electric-ice-blue/90 text-obsidian"
              onClick={() => navigate('/app/my-tickets')}
            >
              <Ticket className="w-5 h-5 mr-2" />
              View My Ticket
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Underage
  if (result?.error === 'underage') {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6" data-testid="verify-callback-underage">
        <Card className="max-w-md bg-graphite/50 border-soft-amber/30">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-24 h-24 rounded-full bg-soft-amber/10 flex items-center justify-center mx-auto mb-6">
              <ShieldAlert className="w-12 h-12 text-soft-amber" />
            </div>
            <h1 className="text-2xl font-bold text-cloud-white mb-2">Age Requirement Not Met</h1>
            <p className="text-soft-silver mb-6">
              This event requires attendees to be at least {result.min_age_required} years old.
            </p>
            
            <div className="bg-obsidian/50 rounded-xl p-4 mb-6">
              <p className="text-muted-text text-sm">Minimum Age Required</p>
              <p className="text-2xl font-bold text-soft-amber">
                {result.min_age_required}+
              </p>
            </div>
            
            <Button
              variant="outline"
              className="w-full border-white/20 text-cloud-white"
              onClick={() => navigate('/app/my-tickets')}
            >
              Go to My Tickets
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Aborted - Can Retry
  if (result?.status === 'aborted' || result?.can_retry) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6" data-testid="verify-callback-retry">
        <Card className="max-w-md bg-graphite/50 border-soft-amber/30">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-24 h-24 rounded-full bg-soft-amber/10 flex items-center justify-center mx-auto mb-6">
              <RefreshCw className="w-12 h-12 text-soft-amber" />
            </div>
            <h1 className="text-2xl font-bold text-cloud-white mb-2">Verification Cancelled</h1>
            <p className="text-soft-silver mb-6">
              {result.message || 'The verification was cancelled. You can try again.'}
            </p>
            
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full bg-electric-ice-blue hover:bg-electric-ice-blue/90 text-obsidian"
                onClick={handleRetry}
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Try Again
              </Button>
              
              <Button
                variant="outline"
                className="w-full border-white/20 text-cloud-white"
                onClick={() => navigate('/app/my-tickets')}
              >
                Go to My Tickets
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Failed
  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6" data-testid="verify-callback-failed">
      <Card className="max-w-md bg-graphite/50 border-coral-red/30">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="w-24 h-24 rounded-full bg-coral-red/10 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-12 h-12 text-coral-red" />
          </div>
          <h1 className="text-2xl font-bold text-cloud-white mb-2">Verification Failed</h1>
          <p className="text-soft-silver mb-6">
            {result?.message || 'We could not verify your age. Please try again with a valid ID.'}
          </p>
          
          <div className="space-y-3">
            <Button
              size="lg"
              className="w-full"
              onClick={handleRetry}
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Try Again
            </Button>
            
            <Button
              variant="outline"
              className="w-full border-white/20 text-cloud-white"
              onClick={() => navigate('/app/my-tickets')}
            >
              Go to My Tickets
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
